import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TStickyBannerMgmt } from '../../../type';
import { ServerMeta } from 'types/common';

interface MedicationContentModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TStickyBannerMgmt) => void;
    condition?: ServerMeta;
    banner?: TStickyBannerMgmt;
}

const SafetyInfoContentModal: React.FC<MedicationContentModalProps> = ({
    visible,
    onCancel,
    onSave,
    condition,
    banner,
}) => {
    const [form] = Form.useForm<TStickyBannerMgmt>();
    const [charCounts, setCharCounts] = useState({
        title: 0,
        subText: 0,
        callToActionName: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TStickyBannerMgmt>) => {
        setCharCounts({
            title: (data.title || '').length,
            subText: (data.subText || '').length,
            callToActionName: (data.callToActionName || '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (banner) {
                form.setFieldsValue(banner);
                updateCharCounts(banner);
            } else {
                // 设置默认值
                const defaultValues = {
                    title: '',
                    subText: '',
                    callToActionName: '',
                    callToActionLink: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    title: 0,
                    subText: 0,
                    callToActionName: 0,
                });
            }
        }
    }, [visible, banner, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // 过滤掉空的考虑事项
            onSave(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TStickyBannerMgmt, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'title') {
            newCounts.title = value.length;
        } else if (fieldName === 'subText') {
            newCounts.subText = value.length;
        } else if (fieldName === 'callToActionName') {
            newCounts.callToActionName = value.length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={`Edit ${condition?.content}`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* Critical Safety Information Field */}
                <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                        { required: true, message: 'Please enter title' },
                        { max: 200, message: 'Title cannot exceed 200 characters' },
                    ]}
                >
                    <Input
                        placeholder="Enter title"
                        maxLength={200}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                    />
                </Form.Item>

                {/* Sub Text Field */}
                <Form.Item
                    label="Sub text"
                    name="subText"
                    rules={[
                        { required: true, message: 'Please enter sub text' },
                        { max: 200, message: 'Sub text cannot exceed 200 characters' },
                    ]}
                >
                    <Input
                        placeholder="Enter sub text"
                        maxLength={200}
                        onChange={(e) => handleFieldChange('subText', e.target.value)}
                    />
                </Form.Item>

                {/* Call to action name Field */}
                <Form.Item
                    label="Call to action name"
                    name="callToActionName"
                    rules={[
                        { required: true, message: 'Please enter call to action name' },
                        { max: 20, message: 'Call to action name cannot exceed 20 characters' },
                    ]}
                >
                    <Input
                        placeholder="Enter call to action name"
                        maxLength={20}
                        onChange={(e) => handleFieldChange('callToActionName', e.target.value)}
                    />
                </Form.Item>
                <Form.Item
                    label="Call to action link"
                    name="callToActionLink"
                >
                    <Input
                        placeholder="Enter call to action link"
                        onChange={(e) => handleFieldChange('callToActionLink', e.target.value)}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SafetyInfoContentModal;
